import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { widgetMeta } from './registry';
import { ZONES } from './widgets/SimpleWidgets';

// Generic "Configure…" form driven by the widget's config schema in the registry.
export default function WidgetConfig({ widget, onSave, onClose }) {
  const meta = widgetMeta(widget.type);
  const [cfg, setCfg] = useState({ ...(widget.config || {}) });
  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  return (
    <div className="fixed inset-0 z-[95] bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose} data-testid="widget-config-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2"><meta.icon className="h-4 w-4 text-slate-600" /><h3 className="font-bold text-slate-900">{meta.title} settings</h3></div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="widget-config-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <label className="block text-xs font-semibold text-slate-600">Tile title (optional)
            <input value={cfg.title_override || ''} onChange={e => set('title_override', e.target.value)} placeholder={meta.title} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg" data-testid="cfg-title" />
          </label>
          {(meta.config || []).map(f => (
            <div key={f.k}>
              {f.type === 'text' && <label className="block text-xs font-semibold text-slate-600">{f.label}<input value={cfg[f.k] || ''} onChange={e => set(f.k, e.target.value)} placeholder={f.placeholder} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg" data-testid={`cfg-${f.k}`} /></label>}
              {f.type === 'bool' && <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={cfg[f.k] !== false && (cfg[f.k] ?? f.k === 'seconds')} onChange={e => set(f.k, e.target.checked)} className="h-4 w-4 accent-blue-600" data-testid={`cfg-${f.k}`} />{f.label}</label>}
              {f.type === 'zones' && <Zones value={cfg.zones?.length ? cfg.zones : [{ tz: 'America/Toronto', label: 'Shop' }]} onChange={v => set('zones', v)} />}
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100" data-testid="cfg-cancel">Cancel</button>
          <button onClick={() => onSave(cfg)} className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold" data-testid="cfg-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function Zones({ value, onChange }) {
  const [tz, setTz] = useState(ZONES[1][0]);
  return (
    <div>
      <p className="text-xs font-semibold text-slate-600">Time zones <span className="font-normal text-slate-400">(first one is the big clock)</span></p>
      <ul className="mt-1 space-y-1">
        {value.map((z, i) => (
          <li key={z.tz} className="flex items-center gap-2 text-sm">
            <input value={z.label} onChange={e => onChange(value.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="w-28 px-2 py-1 text-xs border border-slate-300 rounded" data-testid={`cfg-zone-label-${i}`} />
            <span className="text-xs text-slate-500 flex-1 truncate">{z.tz}</span>
            {value.length > 1 && <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`cfg-zone-remove-${i}`}><X className="h-3.5 w-3.5" /></button>}
          </li>
        ))}
      </ul>
      <div className="flex gap-1 mt-2">
        <select value={tz} onChange={e => setTz(e.target.value)} className="flex-1 text-xs border border-slate-300 rounded px-2 py-1" data-testid="cfg-zone-select">
          {ZONES.filter(([z]) => !value.some(v => v.tz === z)).map(([z, l]) => <option key={z} value={z}>{l} — {z}</option>)}
        </select>
        <button onClick={() => { const l = ZONES.find(([z]) => z === tz)?.[1] || tz; if (!value.some(v => v.tz === tz)) onChange([...value, { tz, label: l }]); }} className="px-2 rounded bg-slate-900 text-white" data-testid="cfg-zone-add"><Plus className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}
